<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { useField, useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import _ from 'lodash';

import LanguageSelector from '@/components/LanguageSelector.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import { useLanguageValidationErrors } from '@/composables';
import { Constants } from '@/constants';
import { getAccountTypeOptions } from '@/helpers/accountType.helper';
import { languageEnabledArray, localizableString } from '@/helpers/validation.helper';
import { getSupportedLocales } from '@/plugins/i18n';
import type { CommunityDto, Locales } from '@/services/types/api.types';
import { useCommunityStore } from '@/stores/community.store';
import { provideEditLocale } from '@/composables/data-display-utils';

const props = defineProps<{
  mode: 'add' | 'edit';
  community: CommunityDto;
}>();
defineEmits([...useDialogPluginComponent.emits]);

const { t } = useI18n();
const communityStore = useCommunityStore();

const languageEnabled = ref<Locales[]>(props.community.language_enabled);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const { handleSubmit, errors, meta, setFieldValue } = useForm({
  initialValues: props.community,
  validationSchema: computed(() =>
    yup.object<CommunityDto>({
      title: localizableString(languageEnabled.value),
      description: localizableString(languageEnabled.value),
      language_enabled: languageEnabledArray(),
      default_language: yup.string().length(2).oneOf(languageEnabled.value).required(),
      // Chosen at creation only: a community is a customer's by default, and a
      // superadmin then needs a customer's request to enter it (COCO-3425).
      ...(props.mode === 'add' && { account_type: yup.number().required() }),
    }),
  ),
});
const languageErrors = useLanguageValidationErrors(errors);

const dialogTitle = computed(() =>
  props.mode === 'add' ? t('form.dialogs.community.create') : t('form.dialogs.community.edit'),
);
const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  if (await communityStore.saveCommunity(values)) {
    onDialogOK();
  } else {
    console.log('error saving');
  }
});

const { value: languageEnabledFieldValue } = useField<Locales[]>('language_enabled');
const { value: defaultLanguage } = useField<Locales>('default_language');
const editLocale = provideEditLocale(languageEnabledFieldValue);

watch(languageEnabledFieldValue, () => {
  if (!languageEnabledFieldValue.value) return;
  if (!languageEnabledFieldValue.value.includes(defaultLanguage.value)) {
    setFieldValue('default_language', null!);
  }
  getSupportedLocales()
    .filter((locale) => !languageEnabledFieldValue.value.includes(locale))
    .forEach((locale) => {
      setFieldValue(`title.${locale}`, undefined);
      setFieldValue(`description.${locale}`, undefined);
    });
  languageEnabled.value = languageEnabledFieldValue.value;
});

function getLanguages(): { label: string; value: string }[] {
  return languageEnabledFieldValue.value.map((locale) => {
    return { label: _.capitalize(t(`languages.${locale}`)), value: locale };
  });
}
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabledFieldValue"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <language-selector name="language_enabled" :label="t('community.language_enabled')" />
        <localized-select
          name="default_language"
          :label="$t('community.default-language')"
          :editLocale="editLocale"
          :options="getLanguages()"
          emit-value
          map-options
          outlined
        />
        <localized-select
          v-if="props.mode === 'add'"
          name="account_type"
          :label="t('customerOverview.accountType.label')"
          :options="getAccountTypeOptions()"
          emit-value
          map-options
          outlined
        />
        <localized-input
          name="title"
          :label="t('community.title')"
          :editLocale="editLocale"
          :language_enabled="languageEnabledFieldValue"
          outlined
        />
        <localized-input
          name="description"
          :label="t('community.description')"
          :editLocale="editLocale"
          :language_enabled="languageEnabledFieldValue"
          outlined
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" :disabled="!meta.valid" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
